import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { TASK_STATUSES, type TaskStatus, type TaskTag } from '@/shared/types/taskQueue.types';
import type { TaskListRowDto } from '../../schemas/tasks.schemas';
import {
  approveTasksBulk,
  archiveTasksBulk,
  getApiErrorMessage,
  listTaskTags,
  listTasks,
} from '../../services/tasksAPI';
import { buildTaskDetailNavState } from './taskDetailNavState';
import './TaskQueue.css';

const OPEN_FILTER: TaskStatus[] = ['available', 'released', 'assigned'];

type StatusFilter = 'open' | 'proposed' | 'archived' | 'all';
type AssigneeFilter = 'any' | 'mine' | 'unassigned';

function parseStatusFilter(raw: string | null): StatusFilter {
  if (raw === 'proposed' || raw === 'archived' || raw === 'all') return raw;
  return 'open';
}

function parseAssigneeFilter(raw: string | null): AssigneeFilter {
  if (raw === 'mine' || raw === 'unassigned') return raw;
  return 'any';
}

function parseTagIds(raw: string | null): string[] {
  if (!raw?.trim()) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

const TaskQueuePage: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageTitle('Task Queue');

  const statusFilter = parseStatusFilter(searchParams.get('status'));
  const assigneeFilter = parseAssigneeFilter(searchParams.get('assignee'));
  const search = searchParams.get('q') ?? '';
  const selectedTagIds = parseTagIds(searchParams.get('tags'));

  const [tasks, setTasks] = useState<TaskListRowDto[]>([]);
  const [tags, setTags] = useState<TaskTag[]>([]);
  const [message, setMessage] = useState('');
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fromPath = `${location.pathname}${location.search}`;

  const listParams = useMemo(() => {
    let status: TaskStatus | TaskStatus[] | undefined;
    if (statusFilter === 'open') status = OPEN_FILTER;
    else if (statusFilter === 'proposed') status = 'proposed';
    else if (statusFilter === 'archived') status = 'archived';
    else if (statusFilter === 'all') status = [...TASK_STATUSES];
    return {
      status,
      tags: selectedTagIds.length ? selectedTagIds : undefined,
      assignee: assigneeFilter === 'any' ? undefined : assigneeFilter,
      q: search.trim() || undefined,
    };
  }, [statusFilter, selectedTagIds, assigneeFilter, search]);

  const refreshList = useCallback(async (): Promise<void> => {
    const rows = await listTasks(listParams);
    setTasks(rows);
  }, [listParams]);

  useEffect(() => {
    void listTaskTags().then(setTags).catch(() => undefined);
  }, []);

  useEffect(() => {
    setMessage('');
    refreshList().catch((err: unknown) => setMessage(getApiErrorMessage(err)));
  }, [refreshList]);

  const updateSearchParams = (updates: {
    status?: StatusFilter;
    assignee?: AssigneeFilter;
    q?: string;
    tags?: string[];
  }): void => {
    const next = new URLSearchParams(searchParams);
    const status = updates.status ?? statusFilter;
    const assignee = updates.assignee ?? assigneeFilter;
    const q = updates.q ?? search;
    const tagList = updates.tags ?? selectedTagIds;

    if (status === 'open') next.delete('status');
    else next.set('status', status);

    if (assignee === 'any') next.delete('assignee');
    else next.set('assignee', assignee);

    const trimmedQ = q.trim();
    if (trimmedQ) next.set('q', trimmedQ);
    else next.delete('q');

    if (tagList.length) next.set('tags', tagList.join(','));
    else next.delete('tags');

    setSearchParams(next, { replace: true });
  };

  const toggleTag = (tagId: string): void => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];
    updateSearchParams({ tags: next });
  };

  const openTask = (taskId: string): void => {
    navigate(`/tasks/${taskId}`, {
      state: buildTaskDetailNavState(fromPath, tasks),
    });
  };

  const openNewTask = (): void => {
    navigate('/tasks/new', { state: { fromPath } });
  };

  const toggleBulk = (taskId: string): void => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const runBulk = async (
    run: (ids: string[]) => Promise<number>,
    verb: string,
  ): Promise<void> => {
    if (bulkSelected.size === 0 || bulkBusy) return;
    const requested = bulkSelected.size;
    setBulkBusy(true);
    setMessage('');
    try {
      const done = await run([...bulkSelected]);
      setBulkSelected(new Set());
      if (done < requested) {
        setMessage(`${verb} ${done} of ${requested} tasks; the rest had changed state.`);
      }
      await refreshList();
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkApprove = (): Promise<void> => runBulk(approveTasksBulk, 'Approved');

  const bulkArchive = (): Promise<void> => runBulk(archiveTasksBulk, 'Archived');

  return (
    <div className="task-queue-page">
      <h1>Task Queue</h1>
      {message ? <p className="task-message">{message}</p> : null}

      <div className="task-filters">
        <input
          type="search"
          placeholder="Search title or description"
          value={search}
          onChange={(e) => updateSearchParams({ q: e.target.value })}
          aria-label="Search tasks"
        />
        <select
          value={statusFilter}
          onChange={(e) =>
            updateSearchParams({ status: e.target.value as StatusFilter })
          }
          aria-label="Status filter"
        >
          <option value="open">Open</option>
          {isAdmin ? <option value="proposed">Proposed</option> : null}
          <option value="archived">Archived</option>
          <option value="all">All</option>
        </select>
        <select
          value={assigneeFilter}
          onChange={(e) =>
            updateSearchParams({ assignee: e.target.value as AssigneeFilter })
          }
          aria-label="Assignee filter"
        >
          <option value="any">Any assignee</option>
          <option value="mine">Mine</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <button type="button" className="primary" onClick={openNewTask}>
          New task
        </button>
        {isAdmin && statusFilter === 'proposed' ? (
          <div className="task-bulk-actions" role="group" aria-label="Bulk actions">
            <button
              type="button"
              disabled={bulkBusy || bulkSelected.size === 0}
              onClick={() => void bulkApprove()}
            >
              Approve selected ({bulkSelected.size})
            </button>
            <button
              type="button"
              disabled={bulkBusy || bulkSelected.size === 0}
              onClick={() => void bulkArchive()}
            >
              Archive selected ({bulkSelected.size})
            </button>
          </div>
        ) : null}
      </div>

      <div className="task-tag-filter" role="group" aria-label="Tag filters">
        {tags.filter((t) => !t.retired).map((tag) => (
          <button
            key={tag.id}
            type="button"
            className={`task-tag-chip ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
            onClick={() => toggleTag(tag.id)}
          >
            {tag.name}
          </button>
        ))}
      </div>

      <ul className="task-list">
        {tasks.map((task) => (
          <li
            key={task.id}
            className={`task-list-item ${task.status === 'released' ? 'released' : ''}`}
          >
            {isAdmin && statusFilter === 'proposed' ? (
              <input
                type="checkbox"
                checked={bulkSelected.has(task.id)}
                onChange={() => toggleBulk(task.id)}
                aria-label={`Select ${task.title}`}
              />
            ) : null}
            <button
              type="button"
              className="task-list-item-button"
              onClick={() => openTask(task.id)}
            >
              {task.status !== 'assigned' && statusFilter !== 'proposed' ? (
                <span className="task-status-badge">{task.status}</span>
              ) : null}
              {task.status === 'released' ? (
                <span className="task-status-badge">Handoff</span>
              ) : null}
              <strong>{task.title}</strong>
              <div className="task-list-item-meta">
                Assigned to:{' '}
                {task.assigneeName ??
                  (task.assigneeId ? 'Unknown assignee' : 'Unassigned')}
              </div>
              {task.tagNames.length ? (
                <div className="task-list-item-meta">{task.tagNames.join(', ')}</div>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TaskQueuePage;
